import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import {
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  Lock,
  Send,
  ThumbsUp,
  ThumbsDown,
  Plus,
  Trash2,
  Loader2,
  ClipboardList,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  CalendarDays,
  User,
  Brain,
  XCircle,
  FileDown,
  Gauge,
  Shield,
  TrendingUp,
  Ban,
  AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  in_progress: { label: "In Progress", color: "text-amber-400", bg: "bg-amber-400/10", icon: Clock },
  ready_for_review: { label: "Ready for Review", color: "text-blue-400", bg: "bg-blue-400/10", icon: Send },
  pending_approval: { label: "Pending Approval", color: "text-purple-400", bg: "bg-purple-400/10", icon: Clock },
  approved: { label: "Approved", color: "text-emerald-400", bg: "bg-emerald-400/10", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "text-red-400", bg: "bg-red-400/10", icon: AlertTriangle },
  project_closed: { label: "Project Closed", color: "text-muted-foreground", bg: "bg-muted/50", icon: Lock },
};

export default function PhaseGate({ projectId, token, permissions, onPhaseAdvanced }) {
  const [gateStatus, setGateStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const [rejectComments, setRejectComments] = useState("");
  const [approveComments, setApproveComments] = useState("");
  const [showTasks, setShowTasks] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", owner: "", due_date: "" });
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [aiReview, setAiReview] = useState(null);
  const [loadingAiReview, setLoadingAiReview] = useState(false);
  const [showAiReview, setShowAiReview] = useState(false);
  
  // AI Gate Rigor Evaluation state (Phase 3)
  const [gateEvaluation, setGateEvaluation] = useState(null);
  const [loadingEvaluation, setLoadingEvaluation] = useState(false);
  const [showEvaluation, setShowEvaluation] = useState(false);

  const fetchGateStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/gate/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setGateStatus(data);
      }
    } catch (e) {
      console.error("Failed to fetch gate status", e);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    fetchGateStatus();
  }, [fetchGateStatus]);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/gate/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setHistory(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingHistory(false); }
  };

  const runAiReview = async () => {
    setLoadingAiReview(true);
    setShowAiReview(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/gate/ai-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setAiReview(await res.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || "AI review failed");
      }
    } catch (e) {
      toast.error("Failed to run AI review");
      console.error(e);
    } finally {
      setLoadingAiReview(false);
    }
  };

  // AI Gate Rigor Evaluation (Phase 3)
  const runGateEvaluation = async () => {
    setLoadingEvaluation(true);
    setShowEvaluation(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/gate/evaluate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const result = await res.json();
        setGateEvaluation(result);
        // Show toast based on recommendation
        if (result.advancement_recommendation === "do_not_advance") {
          toast.error("Gate evaluation: Advancement blocked");
        } else if (result.advancement_recommendation === "advance_with_risk") {
          toast.warning("Gate evaluation: Proceed with caution");
        } else {
          toast.success("Gate evaluation: Ready for review");
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Gate evaluation failed");
      }
    } catch (e) {
      toast.error("Failed to run gate evaluation");
      console.error(e);
    } finally {
      setLoadingEvaluation(false);
    }
  };

  // Get recommendation styling
  const getRecommendationStyle = (rec) => {
    switch (rec) {
      case "advance":
        return { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", icon: CheckCircle2 };
      case "advance_with_risk":
        return { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", icon: AlertTriangle };
      case "do_not_advance":
        return { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", icon: Ban };
      default:
        return { bg: "bg-slate-500/10", border: "border-slate-500/30", text: "text-slate-400", icon: AlertCircle };
    }
  };

  // Get risk level styling
  const getRiskStyle = (level) => {
    switch (level) {
      case "low":
        return "bg-emerald-400/20 text-emerald-400";
      case "medium":
        return "bg-amber-400/20 text-amber-400";
      case "high":
        return "bg-orange-400/20 text-orange-400";
      case "critical":
        return "bg-red-400/20 text-red-400";
      default:
        return "bg-slate-400/20 text-slate-400";
    }
  };

  const createTask = async () => {
    if (!newTask.title.trim()) return;
    try {
      const res = await fetch(`${API}/projects/${projectId}/gate/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newTask),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      setNewTask({ title: "", owner: "", due_date: "" });
      toast.success("Task created");
      fetchGateStatus();
    } catch (e) { toast.error(e.message); }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await fetch(`${API}/projects/${projectId}/gate/tasks/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      fetchGateStatus();
    } catch (e) { toast.error("Failed to update task"); }
  };

  const deleteTask = async (taskId) => {
    try {
      await fetch(`${API}/projects/${projectId}/gate/tasks/${taskId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Task deleted");
      fetchGateStatus();
    } catch (e) { toast.error("Failed to delete task"); }
  };

  const submitForReview = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/gate/submit-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ notes: reviewNotes }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to submit");
      }
      toast.success("Submitted for gate review");
      setReviewNotes("");
      fetchGateStatus();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const approveGate = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/gate/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ decision: "approved", comments: approveComments }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      toast.success("Phase approved! Advancing to next phase.");
      setApproveComments("");
      fetchGateStatus();
      if (onPhaseAdvanced) onPhaseAdvanced();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const rejectGate = async () => {
    if (!rejectComments.trim()) {
      toast.error("Please provide rejection comments");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/gate/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ decision: "rejected", comments: rejectComments }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      toast.info("Phase review rejected with feedback");
      setRejectComments("");
      fetchGateStatus();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-muted-foreground text-sm">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading gate status...
      </div>
    );
  }

  if (!gateStatus) return null;

  const status = STATUS_CONFIG[gateStatus.gate_status] || STATUS_CONFIG.in_progress;
  const StatusIcon = status.icon;
  const canSubmitReview = gateStatus.gate_status === "ready_for_review" && permissions?.can_advance_phase;
  const canApproveReject = gateStatus.gate_status === "pending_approval" && permissions?.can_advance_phase;
  const artifacts = gateStatus.artifacts?.artifacts || {};
  const requiredArtifacts = gateStatus.artifacts?.required || [];
  const tasks = gateStatus.tasks || [];

  return (
    <div className="space-y-3" data-testid="phase-gate-panel">
      {/* Gate Status Badge */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${status.bg}`} data-testid="gate-status-badge">
        <StatusIcon className={`w-4 h-4 ${status.color}`} />
        <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
      </div>

      {/* Artifact Checklist */}
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Required Artifacts</p>
        {requiredArtifacts.map((key) => (
          <div key={key} className="flex items-center gap-2 text-sm" data-testid={`artifact-check-${key}`}>
            {artifacts[key] ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            ) : (
              <Circle className="w-3.5 h-3.5 text-muted-foreground" />
            )}
            <span className={artifacts[key] ? "text-emerald-400" : "text-muted-foreground"}>
              {key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </span>
          </div>
        ))}
      </div>

      {/* Tasks Section */}
      <div className="space-y-2">
        <button
          onClick={() => setShowTasks(!showTasks)}
          className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider font-medium w-full hover:text-foreground transition-colors"
          data-testid="toggle-tasks-btn"
        >
          <ClipboardList className="w-3.5 h-3.5" />
          <span>Tasks ({tasks.length})</span>
          {showTasks ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
        </button>

        {showTasks && (
          <div className="space-y-2 pl-1">
            {tasks.map((task) => (
              <div key={task.task_id} className="flex items-start gap-2 group" data-testid={`task-${task.task_id}`}>
                <button
                  onClick={() => updateTaskStatus(task.task_id, task.status === "completed" ? "pending" : "completed")}
                  className="mt-0.5 flex-shrink-0"
                  data-testid={`task-toggle-${task.task_id}`}
                >
                  {task.status === "completed" ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <Circle className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-tight ${task.status === "completed" ? "line-through text-muted-foreground" : ""}`}>
                    {task.title}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {task.owner && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <User className="w-2.5 h-2.5" />{task.owner}
                      </span>
                    )}
                    {task.due_date && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <CalendarDays className="w-2.5 h-2.5" />{task.due_date}
                      </span>
                    )}
                  </div>
                </div>
                {permissions?.can_edit && (
                  <button
                    onClick={() => deleteTask(task.task_id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`task-delete-${task.task_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                  </button>
                )}
              </div>
            ))}

            {/* Add Task Form */}
            {permissions?.can_edit && (
              <div className="space-y-2 pt-1 border-t border-border/50">
                <Input
                  placeholder="New task..."
                  value={newTask.title}
                  onChange={(e) => setNewTask((p) => ({ ...p, title: e.target.value }))}
                  className="h-8 text-sm"
                  data-testid="new-task-title"
                  onKeyDown={(e) => e.key === "Enter" && createTask()}
                />
                <div className="flex gap-2">
                  <Input
                    placeholder="Owner"
                    value={newTask.owner}
                    onChange={(e) => setNewTask((p) => ({ ...p, owner: e.target.value }))}
                    className="h-7 text-xs flex-1"
                    data-testid="new-task-owner"
                  />
                  <Input
                    type="date"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask((p) => ({ ...p, due_date: e.target.value }))}
                    className="h-7 text-xs flex-1"
                    data-testid="new-task-due-date"
                  />
                </div>
                <Button size="sm" variant="outline" onClick={createTask} className="w-full h-7 text-xs gap-1" data-testid="add-task-btn">
                  <Plus className="w-3 h-3" /> Add Task
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rejection Feedback */}
      {gateStatus.gate_status === "rejected" && gateStatus.review?.comments && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 space-y-1" data-testid="rejection-feedback">
          <p className="text-xs font-medium text-red-400">Rejection Feedback</p>
          <p className="text-sm text-red-300">{gateStatus.review.comments}</p>
          {gateStatus.review.reviewed_by_name && (
            <p className="text-xs text-red-400/70">— {gateStatus.review.reviewed_by_name}</p>
          )}
        </div>
      )}

      {/* AI Pre-Review Scorecard */}
      {gateStatus.gate_status !== "project_closed" && gateStatus.gate_status !== "approved" && (
        <div className="space-y-2 pt-2 border-t border-border/50" data-testid="ai-review-section">
          <Button
            onClick={runAiReview}
            disabled={loadingAiReview}
            size="sm"
            variant="outline"
            className="w-full gap-2 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 hover:text-cyan-300"
            data-testid="ai-review-btn"
          >
            {loadingAiReview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Brain className="w-3.5 h-3.5" />}
            AI Pre-Review
          </Button>

          {showAiReview && aiReview && (
            <div className="space-y-2 animate-in fade-in duration-300" data-testid="ai-review-scorecard">
              {/* Readiness Badge */}
              <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                aiReview.readiness === "strong" ? "bg-emerald-400/10" :
                aiReview.readiness === "ready" ? "bg-emerald-400/10" :
                aiReview.readiness === "conditional" ? "bg-amber-400/10" :
                aiReview.readiness === "at_risk" ? "bg-orange-400/10" :
                "bg-red-400/10"
              }`} data-testid="ai-readiness-badge">
                <div className="flex items-center gap-2">
                  {aiReview.readiness === "strong" || aiReview.readiness === "ready" ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  ) : aiReview.readiness === "conditional" ? (
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-400" />
                  )}
                  <span className={`text-xs font-semibold uppercase tracking-wider ${
                    aiReview.readiness === "strong" || aiReview.readiness === "ready" ? "text-emerald-400" :
                    aiReview.readiness === "conditional" ? "text-amber-400" :
                    "text-red-400"
                  }`}>{aiReview.readiness.replace("_", " ")}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {aiReview.summary.passed}/{aiReview.summary.total}
                </span>
              </div>

              {/* Verdict */}
              <p className="text-xs text-muted-foreground leading-relaxed" data-testid="ai-verdict">
                {aiReview.verdict}
              </p>

              {/* AI Commentary */}
              {aiReview.ai_commentary && (
                <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-2.5" data-testid="ai-commentary">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Brain className="w-3 h-3 text-cyan-400" />
                    <span className="text-[10px] font-medium text-cyan-400 uppercase tracking-wider">Black Belt Assessment</span>
                  </div>
                  <p className="text-xs text-cyan-100/80 leading-relaxed">{aiReview.ai_commentary}</p>
                </div>
              )}

              {/* Criteria Scores */}
              <div className="space-y-1">
                {aiReview.scores.map((s) => (
                  <div key={s.id} className="group" data-testid={`ai-score-${s.id}`}>
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5 flex-shrink-0">
                        {s.rating === "pass" ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                        ) : s.rating === "warning" ? (
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-red-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-xs font-medium ${
                            s.rating === "pass" ? "text-emerald-400" :
                            s.rating === "warning" ? "text-amber-400" :
                            "text-red-400"
                          }`}>{s.name}</span>
                          {s.weight === "critical" && (
                            <span className="text-[9px] px-1 py-0 rounded bg-red-500/20 text-red-400 font-medium">CRITICAL</span>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{s.feedback}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowAiReview(false)}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center py-1"
                data-testid="close-ai-review"
              >
                Dismiss scorecard
              </button>
            </div>
          )}
        </div>
      )}

      {/* AI Gate Rigor Evaluation (Phase 3) */}
      {gateStatus.gate_status !== "project_closed" && gateStatus.gate_status !== "approved" && (
        <div className="space-y-2 pt-2 border-t border-border/50" data-testid="gate-evaluation-section">
          <Button
            onClick={runGateEvaluation}
            disabled={loadingEvaluation}
            size="sm"
            variant="outline"
            className="w-full gap-2 border-violet-500/30 text-violet-400 hover:bg-violet-500/10 hover:text-violet-300"
            data-testid="gate-evaluation-btn"
          >
            {loadingEvaluation ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gauge className="w-3.5 h-3.5" />}
            AI Gate Evaluation
          </Button>

          {showEvaluation && gateEvaluation && (
            <div className="space-y-3 animate-in fade-in duration-300" data-testid="gate-evaluation-result">
              {/* Score and Recommendation Header */}
              {(() => {
                const recStyle = getRecommendationStyle(gateEvaluation.advancement_recommendation);
                const RecIcon = recStyle.icon;
                return (
                  <div className={`${recStyle.bg} border ${recStyle.border} rounded-lg p-3`} data-testid="evaluation-recommendation">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <RecIcon className={`w-5 h-5 ${recStyle.text}`} />
                        <span className={`text-sm font-bold uppercase ${recStyle.text}`}>
                          {gateEvaluation.advancement_recommendation.replace(/_/g, " ")}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-lg font-bold">{gateEvaluation.gate_rigor_score}</span>
                        <span className="text-xs text-muted-foreground">/100</span>
                      </div>
                    </div>
                    <p className={`text-xs ${recStyle.text}`}>{gateEvaluation.recommendation_rationale}</p>
                  </div>
                );
              })()}

              {/* Risk Badges */}
              <div className="flex flex-wrap gap-2">
                {gateEvaluation.statistical_risk_level !== "not_applicable" && (
                  <div className={`px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1 ${getRiskStyle(gateEvaluation.statistical_risk_level)}`} data-testid="statistical-risk-badge">
                    <Shield className="w-3 h-3" />
                    Statistical Risk: {gateEvaluation.statistical_risk_level.toUpperCase()}
                  </div>
                )}
                <div className={`px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1 ${getRiskStyle(gateEvaluation.financial_confidence_level === "high" ? "low" : gateEvaluation.financial_confidence_level === "low" ? "high" : "medium")}`} data-testid="financial-confidence-badge">
                  <TrendingUp className="w-3 h-3" />
                  Financial: {gateEvaluation.financial_confidence_level.toUpperCase()}
                </div>
              </div>

              {/* Critical Failures */}
              {gateEvaluation.critical_failures?.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5" data-testid="critical-failures">
                  <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-1">Critical Failures</p>
                  {gateEvaluation.critical_failures.map((failure, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-xs text-red-300">
                      <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      {failure}
                    </div>
                  ))}
                </div>
              )}

              {/* Scoring Breakdown */}
              <div className="space-y-1.5" data-testid="scoring-breakdown">
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Scoring Breakdown</p>
                {Object.entries(gateEvaluation.scoring_breakdown || {}).map(([criterion, data]) => (
                  <div key={criterion} className="flex items-center gap-2">
                    <div className="flex-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground capitalize">{criterion.replace(/_/g, " ")}</span>
                        <span className={data.score >= 70 ? "text-emerald-400" : data.score >= 50 ? "text-amber-400" : "text-red-400"}>
                          {data.score}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-700 rounded-full mt-0.5 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${data.score >= 70 ? "bg-emerald-400" : data.score >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                          style={{ width: `${data.score}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-[9px] text-muted-foreground w-8 text-right">{data.weight}%</span>
                  </div>
                ))}
              </div>

              {/* Required Corrections */}
              {gateEvaluation.required_corrections?.length > 0 && (
                <div className="space-y-2" data-testid="required-corrections">
                  <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Required Corrections</p>
                  {gateEvaluation.required_corrections.map((correction, i) => (
                    <div key={i} className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2">
                      <div className="flex items-center gap-1.5 mb-1">
                        <AlertTriangle className="w-3 h-3 text-amber-400" />
                        <span className="text-xs font-medium text-amber-400">{correction.criterion}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">{correction.issue}</p>
                      <p className="text-[11px] text-amber-200 mt-1">
                        <span className="font-medium">Action: </span>{correction.action}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={() => setShowEvaluation(false)}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center py-1"
                data-testid="close-gate-evaluation"
              >
                Dismiss evaluation
              </button>
            </div>
          )}
        </div>
      )}

      {/* Submit for Review */}
      {(canSubmitReview || gateStatus.gate_status === "rejected") && permissions?.can_advance_phase && (
        <div className="space-y-2 pt-2 border-t border-border/50">
          {/* Warning if evaluation says do not advance */}
          {gateEvaluation?.advancement_recommendation === "do_not_advance" && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 mb-2" data-testid="block-warning">
              <div className="flex items-center gap-2">
                <Ban className="w-4 h-4 text-red-400" />
                <span className="text-xs font-medium text-red-400">Submission Blocked</span>
              </div>
              <p className="text-[11px] text-red-300 mt-1">
                Gate evaluation indicates this phase is not ready for review. Address the required corrections first.
              </p>
            </div>
          )}
          
          <Textarea
            placeholder="Review notes (optional)..."
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            rows={2}
            className="text-sm resize-none"
            data-testid="review-notes-input"
          />
          <Button
            onClick={submitForReview}
            disabled={submitting || (!gateStatus.artifacts?.all_met) || gateEvaluation?.advancement_recommendation === "do_not_advance"}
            size="sm"
            className="w-full gap-2"
            data-testid="submit-review-btn"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {gateStatus.gate_status === "rejected" ? "Re-submit for Review" : "Submit for Gate Review"}
          </Button>
        </div>
      )}

      {/* Champion Approval/Rejection */}
      {canApproveReject && (
        <div className="space-y-3 pt-2 border-t border-border/50" data-testid="champion-review-panel">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-purple-400" />
            <p className="text-xs font-medium text-purple-400 uppercase tracking-wider">Champion Review</p>
          </div>

          {gateStatus.review?.notes && (
            <p className="text-xs text-muted-foreground italic">"{gateStatus.review.notes}"</p>
          )}

          <div className="space-y-2">
            <Input
              placeholder="Approval comments (optional)"
              value={approveComments}
              onChange={(e) => setApproveComments(e.target.value)}
              className="h-8 text-sm"
              data-testid="approve-comments-input"
            />
            <Button
              onClick={approveGate}
              disabled={submitting}
              size="sm"
              className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700"
              data-testid="approve-gate-btn"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />}
              Approve & Advance
            </Button>
          </div>

          <div className="space-y-2">
            <Textarea
              placeholder="Rejection comments (required)..."
              value={rejectComments}
              onChange={(e) => setRejectComments(e.target.value)}
              rows={2}
              className="text-sm resize-none"
              data-testid="reject-comments-input"
            />
            <Button
              onClick={rejectGate}
              disabled={submitting || !rejectComments.trim()}
              size="sm"
              variant="destructive"
              className="w-full gap-2"
              data-testid="reject-gate-btn"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsDown className="w-3.5 h-3.5" />}
              Reject with Feedback
            </Button>
          </div>
        </div>
      )}

      {/* Decision History */}
      <div className="pt-2 border-t border-border/50">
        <button
          onClick={() => { setShowHistory(!showHistory); if (!showHistory && history.length === 0) fetchHistory(); }}
          className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
          data-testid="toggle-history-btn"
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Gate History</span>
          {showHistory ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
        </button>

        {showHistory && (
          <div className="mt-2 space-y-2">
            {loadingHistory ? (
              <div className="text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Loading...</div>
            ) : history.length === 0 ? (
              <p className="text-xs text-muted-foreground">No gate reviews yet</p>
            ) : (
              history.map((rev) => (
                <div key={rev.review_id} className="text-xs space-y-1 p-2 bg-muted/30 rounded">
                  <div className="flex items-center justify-between">
                    <span className="font-medium capitalize">{rev.phase} Phase</span>
                    <div className="flex items-center gap-1.5">
                      {rev.status === "approved" && (
                        <button
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API}/projects/${projectId}/export/gate-report/${rev.phase}`, {
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              if (!res.ok) throw new Error("Download failed");
                              const blob = await res.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `gate_report_${rev.phase}_${projectId}.pdf`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                            } catch (e) { toast.error("Failed to download report"); }
                          }}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-cyan-400/15 text-cyan-400 hover:bg-cyan-400/25 transition-colors"
                          data-testid={`download-gate-report-${rev.phase}`}
                          title="Download gate report PDF"
                        >
                          <FileDown className="w-2.5 h-2.5" />PDF
                        </button>
                      )}
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        rev.status === "approved" ? "bg-emerald-400/20 text-emerald-400" :
                        rev.status === "rejected" ? "bg-red-400/20 text-red-400" :
                        "bg-purple-400/20 text-purple-400"
                      }`}>{rev.status}</span>
                    </div>
                  </div>
                  {rev.decision_history?.map((d, i) => (
                    <div key={i} className="text-muted-foreground">
                      <span className="capitalize">{d.action}</span> by {d.by_name}
                      {d.comment && <span className="italic"> — "{d.comment}"</span>}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
